import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { ChevronDown, Plus, LogOut, X, Check } from 'lucide-react';
import { useAuthStore, type SavedAccount } from '@/core/stores/useAuthStore';
import { useI18n } from '@/hooks/useI18n';

function useClickOutside(ref: React.RefObject<HTMLElement | null>, handler: () => void) {
  useEffect(() => {
    const listener = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) handler();
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}

function accountInitial(account: SavedAccount): string {
  return (account.name || account.email || '?')[0].toUpperCase();
}

function accountLabel(account: SavedAccount): string {
  return account.name || account.email;
}

function accountSubLabel(account: SavedAccount): string | null {
  if (account.name) return account.email;
  return null;
}

// ── Shared account row ──

const AccountRow: React.FC<{
  account: SavedAccount;
  isActive: boolean;
  showRemove: boolean;
  size: 'sm' | 'md';
  onSwitch: (id: string) => void;
  onRemove: (id: string) => void;
}> = ({ account, isActive, showRemove, size, onSwitch, onRemove }) => {
  const { t } = useI18n();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const avatarSize = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  const gap = size === 'sm' ? 'gap-2.5' : 'gap-3';
  const padding = size === 'sm' ? 'px-3 py-2' : 'px-4 py-3';
  const nameSize = size === 'sm' ? 'text-sm' : 'text-base font-medium';
  const subLabel = accountSubLabel(account);

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirmRemove) {
      onRemove(account.tenant_id);
    } else {
      setConfirmRemove(true);
      setTimeout(() => setConfirmRemove(false), 3000);
    }
  };

  return (
    <div
      className={`flex items-center ${gap} ${padding} ${size === 'md' ? 'rounded-xl' : ''} transition-colors ${
        isActive ? 'bg-primary-50' : size === 'sm' ? 'hover:bg-slate-50' : ''
      }`}
    >
      <button
        onClick={() => onSwitch(account.tenant_id)}
        className={`flex items-center ${gap} flex-1 min-w-0 cursor-pointer`}
      >
        <div className={`${avatarSize} rounded-full flex items-center justify-center font-bold shrink-0 ${
          isActive
            ? 'bg-primary-500 text-white'
            : 'bg-slate-100 text-slate-500'
        }`}>
          {accountInitial(account)}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className={`${nameSize} text-slate-700 truncate`}>{accountLabel(account)}</div>
          {subLabel && <div className="text-xs text-slate-400 truncate">{subLabel}</div>}
        </div>
        {isActive && <Check className="w-4 h-4 text-primary-500 shrink-0" />}
      </button>
      {showRemove && (
        <button
          onClick={handleRemoveClick}
          className={`shrink-0 transition-all cursor-pointer ${
            confirmRemove
              ? 'px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100'
              : 'p-1 text-slate-300 hover:text-red-500 active:text-red-600'
          }`}
          title={t('account.remove')}
        >
          {confirmRemove
            ? t('account.confirm_remove')
            : <X className={size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
          }
        </button>
      )}
    </div>
  );
};

// ── Desktop: popover above the trigger ──

export const AccountSwitcherDesktop: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, accounts, switchAccount, removeAccount, clearAuth } = useAuthStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = accounts.find(a => a.tenant_id === tenantId);

  useClickOutside(ref, useCallback(() => setOpen(false), []));

  const handleSwitch = (id: string) => {
    if (id !== tenantId) switchAccount(id);
    setOpen(false);
  };

  const handleAddAccount = () => {
    setOpen(false);
    navigate(`/login?returnTo=${encodeURIComponent(location.pathname)}`);
  };

  const handleLogout = () => {
    clearAuth();
    setOpen(false);
    navigate('/login');
  };

  if (!active) return null;
  const subLabel = accountSubLabel(active);

  return (
    <div className="relative min-w-0 flex-1" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2.5 w-full px-3 py-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors group"
      >
        <div className="w-8 h-8 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold shrink-0">
          {accountInitial(active)}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-sm font-medium text-slate-700 truncate">{accountLabel(active)}</div>
          {subLabel && <div className="text-xs text-slate-400 truncate">{subLabel}</div>}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 mb-1.5 w-full bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-50">
          {accounts.map(account => (
            <AccountRow
              key={account.tenant_id}
              account={account}
              isActive={account.tenant_id === tenantId}
              showRemove={accounts.length > 1}
              size="sm"
              onSwitch={handleSwitch}
              onRemove={removeAccount}
            />
          ))}

          <div className="border-t border-slate-100 mt-1 pt-1">
            <button
              onClick={handleAddAccount}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 cursor-pointer transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('account.add')}</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2.5 w-full px-3 py-2 text-sm text-slate-400 hover:bg-slate-50 hover:text-red-500 cursor-pointer transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Mobile header: avatar button + dropdown ──

export const AccountAvatarMobile: React.FC = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, accounts, switchAccount, removeAccount, clearAuth } = useAuthStore();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const active = accounts.find(a => a.tenant_id === tenantId);

  useClickOutside(ref, useCallback(() => setOpen(false), []));

  if (!active) return null;

  const handleSwitch = (id: string) => {
    if (id !== tenantId) switchAccount(id);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="w-10 h-10 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold shrink-0 active:scale-95 transition-transform"
      >
        {accountInitial(active)}
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white border border-slate-200 rounded-xl shadow-xl py-1.5 z-50">
          {accounts.map(account => (
            <AccountRow
              key={account.tenant_id}
              account={account}
              isActive={account.tenant_id === tenantId}
              showRemove={accounts.length > 1}
              size="sm"
              onSwitch={handleSwitch}
              onRemove={removeAccount}
            />
          ))}

          <div className="border-t border-slate-100 mt-1 pt-1">
            <button
              onClick={() => { setOpen(false); navigate(`/login?returnTo=${encodeURIComponent(location.pathname)}`); }}
              className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-slate-500 hover:bg-slate-50 cursor-pointer transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>{t('account.add')}</span>
            </button>
            <button
              onClick={() => { clearAuth(); setOpen(false); navigate('/login'); }}
              className="flex items-center gap-2.5 w-full px-3 py-2.5 text-sm text-slate-400 hover:bg-slate-50 hover:text-red-500 cursor-pointer transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span>{t('nav.logout')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Mobile: inline list in drawer ──

export const AccountSwitcherMobile: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId, accounts, switchAccount, removeAccount, clearAuth } = useAuthStore();

  const handleSwitch = (id: string) => {
    if (id !== tenantId) switchAccount(id);
    onClose();
  };

  const handleAddAccount = () => {
    onClose();
    navigate(`/login?returnTo=${encodeURIComponent(location.pathname)}`);
  };

  const handleLogout = () => {
    clearAuth();
    onClose();
    navigate('/login');
  };

  return (
    <div className="space-y-1">
      {accounts.map(account => (
        <AccountRow
          key={account.tenant_id}
          account={account}
          isActive={account.tenant_id === tenantId}
          showRemove={accounts.length > 1}
          size="md"
          onSwitch={handleSwitch}
          onRemove={removeAccount}
        />
      ))}

      <button
        onClick={handleAddAccount}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium text-slate-500 hover:bg-white hover:shadow-sm transition-all cursor-pointer"
      >
        <Plus className="w-5 h-5" />
        <span>{t('account.add')}</span>
      </button>

      <button
        onClick={handleLogout}
        className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:bg-white hover:text-red-600 hover:shadow-sm transition-all cursor-pointer"
      >
        <LogOut className="w-5 h-5" />
        <span>{t('nav.logout')}</span>
      </button>
    </div>
  );
};
